<script setup>
import { computed, ref } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import * as ContourDefinitions from '@/src/store/contours/contour-definitions';

// Props
const props = defineProps({
  viewerID: { type: String, required: true },
  x: { type: Number, required: true },
  y: { type: Number, required: true },
  show: { type: Boolean, required: true },
  imageUID: { type: String, required: true },
  currentSeriesIndex: { type: Number, required: true },
  currentSliceIndex: { type: Number, required: true },
  phaseNumber: { type: Number, required: true },
  selectedImages: { type: Array, required: true },
});

// Emits
const emit = defineEmits(['update:show']);

const contourContainerStore = useContourContainerStore();

// Composables
const viewportActions = useViewportActions();
const backendSegmentation = useBackendSegmentation();

// Reactive state
const localStartPhase = ref(null);
const localEndPhase = ref(null);
const virtualActivator = ref(null);

const hasSelectedImages = computed(() => {
  return props.selectedImages && props.selectedImages.length > 0;
});

// Computed properties
const hasContours = computed(() => {
  return viewportActions.imageHasContours(props.imageUID);
});

const getImageDrawingContours = computed(() => {
  return viewportActions.getImageDrawingContours(props.imageUID);
});

const contourLabelToIcon = (label) => {
  return ContourDefinitions.ContourDefinitions[label]?.icon || null;
};

const clipboardEmpty = computed(() => {
  return !viewportActions.clipboardHasContours();
});

const showContextMenu = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

// Methods
function copyContours() {
  viewportActions.copyImageDrawingContours(props.imageUID);
  showContextMenu.value = false;
}

function pasteContours() {
  viewportActions.pasteContours(props.imageUID);
  showContextMenu.value = false;
}

function deleteContour() {
  if (hasSelectedImages.value) {
    contourContainerStore.bulkDeleteAllContours(props.selectedImages);
  }
  else {
    viewportActions.handleDeleteAll(props.imageUID);
  }
  showContextMenu.value = false;
}

function forwardContour(label) {
  viewportActions.forwardContourAcrossPhases(
    props.viewerID,
    props.imageUID,
    label,
  );
  showContextMenu.value = false;
}

function setTrimmerStart() {
  localStartPhase.value = props.phaseNumber;
  viewportActions.updateStartEndBreathHold(
    props.viewerID,
    localStartPhase.value,
    localEndPhase.value,
  );

  localEndPhase.value = null;
  localStartPhase.value = null;
  showContextMenu.value = false;
}

function setTrimmerEnd() {
  localEndPhase.value = props.phaseNumber;
  viewportActions.updateStartEndBreathHold(
    props.viewerID,
    localStartPhase.value,
    localEndPhase.value,
  );

  localEndPhase.value = null;
  localStartPhase.value = null;
  showContextMenu.value = false;
}

function excludeImage() {
  if (hasSelectedImages.value) {
    viewportActions.excludeImages(props.viewerID, props.selectedImages);
  }
  else {
    viewportActions.excludeImage(props.viewerID, props.imageUID);
  }
  showContextMenu.value = false;
}

async function handleSegmentImage() {
  let segmentationResult;
  if (hasSelectedImages.value) {
    segmentationResult = await backendSegmentation.segment({
      viewerIDs: [props.viewerID],
      include: props.selectedImages,
    });
  }
  else {
    segmentationResult = await backendSegmentation.segment({
      viewerIDs: [props.viewerID],
      include: [props.imageUID],
    });
  }
  backendSegmentation.addContourBatch(segmentationResult.results);
  showContextMenu.value = false;
}
</script>

<template>
  <!-- Virtual activator element positioned at click coordinates -->
  <div
    v-show="true"
    ref="virtualActivator"
    :style="{
      position: 'fixed',
      left: `${x}px`,
      top: `${y}px`,
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
    }"
  />

  <v-menu
    v-model="showContextMenu"
    class="custom-context-menu"
    :activator="virtualActivator"
    close-on-content-click
    location="bottom start"
    origin="auto"
    :offset="5"
    :close-on-back="true"
    :max-height="430"
    :max-width="300"
    transition="slide-y-transition"
    :flip="true"
    :shift="true"
  >
    <v-card class="context-menu-card">
      <v-card-title class="context-menu-title">
        Image {{ currentSeriesIndex + 1 }}/{{ currentSliceIndex + 1 }}/{{
          phaseNumber + 1
        }}
      </v-card-title>
      <v-divider />

      <v-list density="compact">
        <v-list-item class="context-menu-item" @click="excludeImage">
          <v-icon>mdi-image-remove-outline</v-icon>
          Toggle exclude
          <span v-if="selectedImages.length > 0">({{ selectedImages.length }}) selected</span>
          from analysis
        </v-list-item>

        <v-divider />

        <v-list-item class="context-menu-item" @click="handleSegmentImage">
          <v-icon>mdi-image-filter-center-focus</v-icon>
          Segment Image<span v-if="selectedImages.length > 0">s ({{ selectedImages.length }}) selected</span>
        </v-list-item>

        <!-- <v-list-item @click="excludeImage" class="context-menu-item">
          <v-icon>mdi-eye-off</v-icon>
          Exclude Image<span v-if="selectedImages.length > 0"
            >s ({{ selectedImages.length }}) selected</span
          >
        </v-list-item> -->

        <v-divider />
        <div class="two-column">
          <v-list-item
            class="context-menu-item"
            :disabled="!hasContours"
            @click="copyContours"
          >
            <v-icon>mdi-content-copy</v-icon>
            Copy Contours
          </v-list-item>

          <v-list-item
            class="context-menu-item"
            :disabled="clipboardEmpty"
            @click="pasteContours"
          >
            <v-icon>mdi-content-paste</v-icon>
            Paste Contours
          </v-list-item>
        </div>
        <v-list-item
          class="context-menu-item"
          :disabled="!hasContours"
          @click="deleteContour"
        >
          <v-icon>mdi-delete</v-icon>
          Delete Contours
        </v-list-item>

        <v-list-item link class="context-menu-item">
          <v-icon>mdi-share-all</v-icon>
          Forward Contours Across Phases
          <template #append>
            <v-icon>mdi-menu-right</v-icon>
          </template>

          <v-menu
            :open-on-focus="false"
            activator="parent"
            open-on-hover
            location="end"
            :close-on-content-click="false"
          >
            <v-list>
              <v-list-item
                v-for="(contour, key) in getImageDrawingContours"
                :key="key"
                class="context-menu-item"
                link
                @click="forwardContour(key)"
              >
                <div class="flex align-center gap-2">
                  <img
                    :src="contourLabelToIcon(key)"
                    :width="18"
                    :height="18"
                    alt="icon"
                  >

                  <h6>{{ key }}</h6>
                </div>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>

        <v-divider />
        <v-list-item class="context-menu-item">
          <div>
            <h6 class="mb-2">
              Breath Hold:
            </h6>
            <v-btn size="small" class="ml-2" @click="setTrimmerStart">
              <v-icon>mdi-scissors-cutting</v-icon>
              Set Start
            </v-btn>
            <v-btn size="small" class="ml-2" @click="setTrimmerEnd">
              <v-icon>mdi-scissors-cutting</v-icon>
              Set End
            </v-btn>
          </div>
        </v-list-item>

        <!-- <v-list-item class="context-menu-item">
          <div>
            <h6 class="mb-2">BMORE:</h6>
            <v-btn size="small" @click="setTrimmerStart" class="ml-2">
              <v-icon>mdi-scissors-cutting</v-icon>
              Set 0s
            </v-btn>
            <v-btn size="small" @click="setTrimmerEnd" class="ml-2">
              <v-icon>mdi-scissors-cutting</v-icon>
              Set 30s
            </v-btn>
          </div>
        </v-list-item> -->
      </v-list>
    </v-card>
  </v-menu>
</template>

<style scoped>
.context-menu-card {
  background-color: var(--md-bg);
  color: var(--md-text-primary);
  display: flex;
  flex-direction: column;
}

.context-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
  padding: 8px 0px;
  min-height: 0px;
}

.context-menu-item:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.two-column {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.manual-input {
  width: 100%;
}

.trimmer-label {
  font-size: 12px;
  margin-right: 8px;
  white-space: nowrap;
}

.trimmer-input {
  margin: 0 4px;
}

.custom-context-menu {
  z-index: 999999;
}

.context-menu-title {
  font-size: 14px;
  background-color: var(--md-bg);
  padding: 8px 16px;
}

.submenu-card {
  background-color: var(--md-bg);
  color: var(--md-text-primary);
  width: 300px;
}
</style>
